import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

// Define service types
export enum ServiceType {
  CORE = 'core',
}

// Define base URL for core service
const CORE_SERVICE_URL = process.env.REACT_APP_CORE_SERVICE_URL || 'http://localhost:3000';

// Default config for axios instances
const defaultConfig: AxiosRequestConfig = {
  timeout: 100000, // 100 seconds
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
};

/** Promise for in-flight token refresh so concurrent 401s only trigger one refresh */
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token');
  }
  const response = await axios.post<{ accessToken: string; refreshToken: string }>(
    `${CORE_SERVICE_URL.replace(/\/$/, '')}/auth/refresh`,
    { refreshToken },
    {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      timeout: 10000,
    }
  );
  const { accessToken, refreshToken: newRefreshToken } = response.data;
  if (!accessToken) {
    throw new Error('Invalid refresh response');
  }
  localStorage.setItem('access_token', accessToken);
  if (newRefreshToken) {
    localStorage.setItem('refresh_token', newRefreshToken);
  }
  return accessToken;
}

function clearAuthAndRedirect(): void {
  refreshPromise = null;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_data');
  window.location.href = '/signin';
}

// Create a class to manage API instances
class ApiService {
  private instances: Record<ServiceType, AxiosInstance> = {} as Record<ServiceType, AxiosInstance>;

  constructor() {
    // Initialize instances for each service
    Object.values(ServiceType).forEach((serviceType) => {
      this.instances[serviceType] = this.createInstance(serviceType);
    });
  }

  // Create an axios instance for a specific service
  private createInstance(serviceType: ServiceType): AxiosInstance {
    const instance = axios.create({
      ...defaultConfig,
      baseURL: CORE_SERVICE_URL,
    });

    // Add request interceptor
    instance.interceptors.request.use(
      (config) => {
        // Get access token from localStorage
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor: on 401, refresh token and retry once
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        const hasRefreshToken = !!localStorage.getItem('refresh_token');
        if (!hasRefreshToken) {
          clearAuthAndRedirect();
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          // One refresh at a time; other 401s wait for the same result
          if (!refreshPromise) {
            refreshPromise = refreshAccessToken();
          }
          const accessToken = await refreshPromise;
          refreshPromise = null;

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          clearAuthAndRedirect();
          return Promise.reject(refreshError);
        }
      }
    );

    return instance;
  }

  // Get the axios instance for a specific service
  public getService(serviceType: ServiceType): AxiosInstance {
    return this.instances[serviceType];
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;